import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTabLabel,
  SidebarTab,
  SidebarTabs,
} from '@trueprodigy/components';
import * as L from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withStateHandlers } from 'recompose';
import styled from 'styled-components';
import LinearProgress from '@material-ui/core/LinearProgress';

import * as globalActions from '@actions/global';
import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import HeaderBackgroundImage from '@components/HeaderBackgroundImage';
import HeaderWrapper from '@components/HeaderWrapper';
import ContentWrapper from '@components/ContentWrapper';
import { fetchingState } from '@constants';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
  userDataSelector,
} from '@selectors/global';

import OwnerLookupModal from './ownerLookupModal';
import OwnerCreateModal from './ownerCreateModal';
import PropertyLookupModal from './propertyLookupModal';
import DeedLookupModal from './deedLookupModal';
import ApplicationLookupModal from './applicationLookupModal';
import QuickAccessProperty from './quickAccessProperty';

import appraisalLogo from '../assets/true-prodigy-logo-Appraisal.png';
import plusIcon from '../assets/icon_plus_gray.png';
import screenIcon from '../assets/PA_Print-Screen.png';
import versionIcon from '../assets/PA_Version.png';
import appsIcon from '../assets/PA_Apps.png';
import abacusIcon from '../assets/PA_abacus.png';
import abacusIconAnim from '../assets/PA_abacus_anim.gif';

import * as propertyActions from '../actions/property';
import * as economicUnitActions from '../actions/economicunits';
import * as globalAppraisalActions from '../actions/global';
import { isRecalculatingSelector } from '../selectors/economicunits';

import {
  AppraisalPageTitle,
  AppraisalRoute,
  economicUnitsMenu,
} from '../constants';
import {
  selectedPropertyRecordSelector,
  currentAppraisalYearSelector,
  selectedPropertyRecordYearSelector,
  selectedPropertyPVersionSelector,
  selectedPropertyPRollCorrSelector,
  selectedAgentRecordSelector,
  selectedOwnerRecordSelector,
  selectedSmartGroupSelector,
  selectedBuilderPlanSelector,
  selectedEconomicUnitSelector,
  selectedEconomicUnitYearSelector,
  selectedEconomicUnitVersionSelector,
  selectedEconomicUnitRollCorrSelector,
} from '../selectors/global';
import {
  propertyRecalculateSelector,
  propertyHeaderSelector,
  propertySelector,
  propertyMenulistSelector,
  getRecalculationJob,
  printCardSelector,
  selectedPersonalDetailIDSelector,
  recalculateTriggerSelector,
} from '../selectors/property';

import { selectedYearSelector } from '@selectors/appraisalDemo/global';
import ability, { defineRules } from '@config/permissions/ability';
import Can, {
  canViewSubject,
  canEditSubject,
  canExecuteSubject,
} from '@config/permissions/Can';
import { flatten } from '../utils';

let normalizedAppraisalRoutes;
const generateNormalizedRoutes = () => {
  normalizedAppraisalRoutes =
    Object.entries(AppraisalRoute).reduce((acc, [key, route]) => {
      acc[key] = route
        .split('/')
        .map((path) => (path.charAt(0) === ':' ? '' : path))
        .join('/')
        .replace(/\/+$/, '')
        .replace(/(\/)\1+/g, '$1');
      return acc;
    }, {}) || null;
};

const getMenuTitle = (menuData, link) => {
  if (!menuData) {
    return '';
  }
  let menuItem;
  menuData.forEach((menu) => {
    menuItem =
      menu.menuDefinition &&
      flatten(menu.menuDefinition).find((menuItem) => menuItem.url === link);
  });
  return menuItem ? menuItem.title : '';
};

const getAppraisalRoute = (path) => {
  const AppraisalRoutesMap = {
    [AppraisalRoute.AppraisalAgentSearch]: canExecuteSubject(
      'AppraisalCreateAgentWalkthru',
    )
      ? {
          title: 'Agent Search',
          create: AppraisalRoute.AppraisalAgentCreate,
        }
      : null,
    [AppraisalRoute.AppraisalOwnerSearch]: canExecuteSubject(
      'AppraisalCreateOwnerWalkthru',
    )
      ? {
          title: 'Owner Search',
          create: AppraisalRoute.AppraisalOwnerCreate,
        }
      : null,
    [AppraisalRoute.AppraisalMortgagelenderSearch]: canExecuteSubject(
      'AppraisalCreateMortgageLenderWalkthru',
    )
      ? {
          title: 'Mortgage Lender Search',
          create: AppraisalRoute.AppraisalMortgagelenderCreate,
        }
      : null,
    [AppraisalRoute.AppraisalBuilderPlansSearch]: canEditSubject(
      'AppraisalBuilderPlans',
    )
      ? {
          title: 'Builder Plans Search',
          create: AppraisalRoute.AppraisalBuilderPlanCreate,
        }
      : null,
    [AppraisalRoute.AppraisalSaleSearch]: {
      title: 'Sale Search',
      create: AppraisalRoute.AppraisalSaleSearch,
    },
  };
  return AppraisalRoutesMap[path];
};

const parseHeaderTitle = (pathname, propertyMenuList) => {
  if (location.pathname === AppraisalRoute.AppraisalPropertySmartGroupsSearch) {
    return 'Smart Group Search';
  }
  if (location.pathname === AppraisalRoute.AppraisalBuilderPlansSearch) {
    return 'Builder Plans Search';
  }
  if (!normalizedAppraisalRoutes) {
    generateNormalizedRoutes();
  }
  const genericPath = pathname
    .split('/')
    .map((path) => (path.match(/[0-9]+/g) ? '' : path))
    .join('/')
    .replace(/\/+$/, '')
    .replace(/(\/)\1+/g, '$1');

  const idPattern = pathname.match(/[0-9]+/g);

  if (L.isNil(idPattern)) {
    return AppraisalPageTitle[pathname];
  }

  const routeIdx = Object.values(normalizedAppraisalRoutes).findIndex(
    (route) => route === genericPath,
  );
  if (routeIdx >= 0) {
    return AppraisalPageTitle[Object.values(AppraisalRoute)[routeIdx]];
  }

  if (genericPath.includes(normalizedAppraisalRoutes.AppraisalPropertyPage)) {
    const [link] = genericPath.split('/').slice(-1);
    return getMenuTitle(propertyMenuList, link);
  }
};

const renderPropertyMenu = (
  activeProperty,
  activePropertyYear,
  activePropertyVersion,
  activePropertyRollCorr,
  data,
) => {
  const menuItems = L.flattenDeep(
    data.map(
      (menu) =>
        menu.menuDefinition &&
        flatten(menu.menuDefinition).map((link) => (
          <SidebarItem
            key={link.url}
            link={`/appraisal/property/${activeProperty}/${activePropertyYear}/${activePropertyVersion}/${activePropertyRollCorr}/${link.url}${window.location.search}`}
            title={link.title}
          />
        )),
    ),
  );

  return (
    menuItems || (
      <SidebarItem
        link={AppraisalRoute.AppraisalMenuProfile}
        title="No property submenu configured"
      />
    )
  );
};

const PageWrapper = ({
  activeAgent,
  activeBuilderPlan,
  activeOwner,
  activeProperty,
  activeSmartGroup,
  activePropertyYear,
  activePropertyVersion,
  activePropertyRollCorr,
  activeEconomicUnit,
  activeEconomicUnitYear,
  activeEconomicUnitVersion,
  activeEconomicUnitRollCorr,
  activeTab,
  children,
  closeTab,
  currentAppraisalYear,
  history,
  isSidebarVisible,
  location,
  persistPreference,
  printCard,
  property,
  propertyDetails,
  propertyMenuList,
  recalculate,
  recalculationJob,
  recalculateEconomicUnit,
  recalculateProperty,
  resetRecalculationJob,
  fetchRecalculatedValues,
  fetchEconUnitRecalcValues,
  checkRecalcProperty,
  fetchPropertyErrors,
  setActiveTab,
  toggleSidebar,
  selectPropertyRecord,
  selectPropertyRecordYear,
  // userRights,
  fetchRecalculationJob,
  quickPropertyLookup,
  userStatus,
  selectedPersonalDetailID,
  fetchBppSubDetails,
  isRecalculating,
  userData,
  setViewOnlyMode,
  recalculateTrigger,
}) => {
  const isReadOnly = userStatus.get('readOnly');
  const office = userData.get('office');
  const usingHistoryDb = office.match(/.+History$/);

  // useEffect(() => {
  //   if (userRights && userRights.toJS() && userRights.toJS().length) {
  //     const rules = defineRules(userRights.toJS());
  //     ability.update(rules);
  //   }
  // }, [userRights]);

  const itemActive =
    activeAgent ||
    activeBuilderPlan ||
    activeOwner ||
    activeProperty ||
    activeSmartGroup ||
    activeEconomicUnit;

  useEffect(() => {
    if (!itemActive && activeTab) {
      setActiveTab(null, 0);
    }
  }, [itemActive]);

  useEffect(() => {
    const data = userData && userData.toJS();
    if (data.userrights && data.userrights.length > 0) {
      const rules = defineRules(data.userrights);
      ability.update(rules);
    }
    if (data.roles && data.roles.length > 0) {
      for (let x = 0; x < data.roles.length; x++) {
        if (data.roles[x] === 'View Only') {
          setViewOnlyMode(true);
        }
      }
    }
  }, [userData]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (recalculateTrigger) {
      recalculatePropertyHandler();
    }
  }, [recalculateTrigger]);

  const buildMenuLink = () => {
    if (activeProperty && activeTab === 1) {
      if (
        propertyMenuList.data &&
        propertyMenuList.data.length > 0 &&
        propertyMenuList.data[0].menuDefinition
      ) {
        return propertyMenuList.data[0].menuDefinition.map(
          (link) =>
            `/appraisal/property/${activeProperty}/${activePropertyYear}/${activePropertyVersion}/${activePropertyRollCorr}/${link.url}`,
        );
      }
    }
    return [];
  };

  const recalculatePropertyHandler = async () => {
    const params = {
      propertyId: activeProperty,
      selectedYear: activePropertyYear,
      pVersion: activePropertyVersion,
      pRollCorr: activePropertyRollCorr,
      pYear: activePropertyYear,
    };
    const { results } = await recalculateProperty(params);
    if (selectedPersonalDetailID !== null) {
      fetchBppSubDetails({
        pDetailID: selectedPersonalDetailID,
      });
    }
    if (results && results.pID) {
      await checkRecalcProperty(params);
      fetchPropertyErrors(params);
    }
    if (results.smartGroup) {
      const recalcJob = results.recalcJob;
      const intervalTime = 5000;

      fetchRecalculationJob({
        jobId: recalcJob,
      });

      const intervalId = setInterval(async () => {
        const { results } = await fetchRecalculationJob({
          jobId: recalcJob,
        });
        if (results[0].recalcStatus === 'Finished' || results[0].error) {
          await checkRecalcProperty(params);
          fetchPropertyErrors(params);
          fetchRecalculatedValues();
          resetRecalculationJob();
          clearInterval(intervalId);
        }
      }, intervalTime);
    } else {
      fetchRecalculatedValues();
    }
  };

  const recalculateEconomicUnitHandler = async () => {
    await recalculateEconomicUnit({
      econId: activeEconomicUnit,
      pYear: activeEconomicUnitYear,
      pVersion: activeEconomicUnitVersion,
      pRollCorr: activeEconomicUnitRollCorr,
    });
    fetchEconUnitRecalcValues();
  };

  return (
    <Wrapper>
      <Helmet>
        <title>Appraisal</title>
      </Helmet>
      <QuickAccessProperty
        history={history}
        year={currentAppraisalYear}
        quickPropertyLookup={quickPropertyLookup}
        persistPreference={persistPreference}
        selectPropertyRecord={selectPropertyRecord}
        selectPropertyRecordYear={selectPropertyRecordYear}
      />
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={appraisalLogo}
        sidebarFooter={
          <SidebarFooter>
            {/* <SidebarRecordWidget /> */}
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="Appraisal" />
            }
          />
          {activeProperty && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Property"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AppraisalRoute.AppraisalPropertySearch);
                  }}
                />
              }
            />
          )}
          {activeAgent && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Agent"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AppraisalRoute.AppraisalAgentSearch);
                  }}
                />
              }
            />
          )}
          {activeOwner && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Owner"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AppraisalRoute.AppraisalOwnerSearch);
                  }}
                />
              }
            />
          )}
          {activeSmartGroup && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Smart Group"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(
                      AppraisalRoute.AppraisalPropertySmartGroupsSearch,
                    );
                  }}
                />
              }
            />
          )}
          {activeBuilderPlan && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Builder Plan"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AppraisalRoute.AppraisalBuilderPlansSearch);
                  }}
                />
              }
            />
          )}
          {activeEconomicUnit && (
            <SidebarTab
              label={
                <LabelWrapper>
                  <SidebarTabLabel
                    hasClose
                    isActive={activeTab === 1}
                    label="Eco Units"
                    onClose={(event) => {
                      event.stopPropagation();
                      closeTab();
                      history.push(AppraisalRoute.AppraisalEconomicUnitsSearch);
                    }}
                  />
                </LabelWrapper>
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 && (
          <>
            <SidebarItem
              link={AppraisalRoute.AppraisalDashboard}
              title="Dashboard"
            />
            <SidebarItem isOpen nested title="Search">
              <SidebarItem
                link={AppraisalRoute.AppraisalPropertySearch}
                title="Property"
              />
              <SidebarItem
                link={AppraisalRoute.AppraisalPropertyEquitySearch}
                title="Property Equity"
              />
              {canViewSubject('AppraisalOwner') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalOwnerSearch}
                  title="Owner"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalAgent') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalAgentSearch}
                  title="Agent"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalLenders') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalMortgagelenderSearch}
                  title="Lender"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalSales') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalSaleSearch}
                  title="Sales"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalDeeds') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalDeedsSearch}
                  title="Deeds"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalSmartGroups') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalPropertySmartGroupsSearch}
                  title="Smart Groups"
                />
              ) : (
                <></>
              )}
              {canViewSubject('AppraisalBuilderPlans') ? (
                <SidebarItem
                  link={AppraisalRoute.AppraisalBuilderPlansSearch}
                  title="Builder Plans"
                />
              ) : (
                <></>
              )}
              <SidebarItem
                isOpen
                nested
                link={AppraisalRoute.AppraisalBuildingPermitsSearch}
                title="Building Permits"
              >
                <SidebarItem
                  link={AppraisalRoute.AppraisalMassRematchListing}
                  title="Mass Import Re-match"
                  wrapperClassName="third-gen-item"
                />
              </SidebarItem>
              <SidebarItem
                link={AppraisalRoute.AppraisalEconomicUnitsSearch}
                title="Economic Units"
              />
              <SidebarItem
                link={AppraisalRoute.AppraisalMobileHomeSearch}
                title="Mobile Homes"
              />
              <SidebarItem
                link={
                  AppraisalRoute.AppraisalElectronicDeliveryRequestSearchTaxpayer
                }
                title="Electronic Requests (Taxpayer)"
              />
              <SidebarItem
                link={
                  AppraisalRoute.AppraisalElectronicDeliveryRequestSearchAgent
                }
                title="Electronic Requests (Agent)"
              />
              <SidebarItem
                link={`${AppraisalRoute.AppraisalIncomingCalls}?phoneNumber=2609692578`}
                title="Incoming Calls"
              />
            </SidebarItem>

            <SidebarItem
              link={AppraisalRoute.AppraisalEvaluation}
              title="Evaluation"
            />
            {!isReadOnly ? (
              <SidebarItem defaultOpen={false} nested title="App Functions">
                <Can I="execute" a="AppraisalCreatePropertyWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalPropertyCreateWalkthrough}
                    title="Create Property"
                  />
                </Can>
                <Can I="execute" a="AppraisalMassCreatePropertyWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalPropertyMassCreate}
                    title="Mass Create Property"
                  />
                </Can>
                <Can I="execute" a="AppraisalCreateOwnerWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalOwnerCreate}
                    title="Create Owner"
                  />
                </Can>
                <Can I="execute" a="AppraisalCreateAgentWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalAgentCreate}
                    title="Create Agent"
                  />
                </Can>
                {/* <Can I="execute" a="CreateAppealWalkthru"> */}
                <SidebarItem
                  link={AppraisalRoute.AppealCreateWalkthrough.replace(
                    ':propertyId',
                    '',
                  )}
                  title="Create Appeal"
                />
                {/* </Can> */}
                <Can I="execute" a="AppraisalAgentMassEntryWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalAgentMassEntryWalkthrough}
                    title="Mass Entry of Agent"
                  />
                </Can>
                <Can I="execute" a="AppraisalCreateMortgageLenderWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalMortgagelenderCreate}
                    title="Create Mortgage"
                  />
                </Can>
                <Can I="execute" a="AppraisalMergePropertiesWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalPropertyMerge}
                    title="Merge Properties"
                  />
                </Can>
                <Can I="execute" a="AppraisalSplitPropertyWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalPropertySplit.replace(
                      '/:propertyId?/:pYear?/:pVersion?/:pRollCorr?',
                      '',
                    )}
                    title="Split Property"
                  />
                </Can>
                <Can I="execute" a="AppraisalOwnershipTransferWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalPropertyTransfer.replace(
                      '/:propertyId?/:pYear?/:pVersion?/:pRollCorr?',
                      '',
                    )}
                    title="Ownership Transfer"
                  />
                </Can>
                <Can I="execute" a="AppraisalRenditionWalkthru">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalRenditionWalkthrough.replace(
                      '/:propertyId?/:pYear?/:pVersion?/:pRollCorr?',
                      '',
                    )}
                    title="Rendition Walkthrough"
                  />
                </Can>
                <Can I="execute" a="AppraisalImportValuations">
                  <SidebarItem
                    link={AppraisalRoute.AppraisalMassImportValuations.replace(
                      '/:propertyId?/:pYear?/:pVersion?/:pRollCorr?',
                      '',
                    )}
                    title="Mass Import Valuations"
                  />
                </Can>
              </SidebarItem>
            ) : (
              <></>
            )}

            {/* <SidebarItem nested title='Walkthroughs'>
            <SidebarItem
              link={''}
              title='TBD'/>
          </SidebarItem> */}
            <SidebarItem nested title="User Settings">
              <SidebarItem
                link={AppraisalRoute.AppraisalMenuProfileDefaults}
                title="Menu Defaults"
              />
            </SidebarItem>
          </>
        )}
        {activeTab === 1 &&
          activeProperty &&
          propertyMenuList &&
          propertyMenuList.data && (
            <SidebarItem isOpen nested title="Property">
              {renderPropertyMenu(
                activeProperty,
                activePropertyYear,
                activePropertyVersion,
                activePropertyRollCorr,
                propertyMenuList.data,
              )}
            </SidebarItem>
          )}
        {activeTab === 1 && activeSmartGroup && (
          <React.Fragment>
            <SidebarItem
              link={`/appraisal/smartgroup/${activeSmartGroup}`}
              title="General"
            />
          </React.Fragment>
        )}
        {activeTab === 1 && activeBuilderPlan && (
          <React.Fragment>
            <SidebarItem
              link={`/appraisal/builderplans/${activeBuilderPlan}/detail`}
              title="Summary"
            />
          </React.Fragment>
        )}
        {activeTab === 1 && activeEconomicUnit && (
          <SidebarItem isOpen nested title="Economic Units">
            {activeEconomicUnit === 'new' ? (
              <SidebarItem
                link="/appraisal/economicunits/create"
                title="Create"
              />
            ) : (
              economicUnitsMenu.map(({ link, title }) => (
                <SidebarItem
                  key={link}
                  link={
                    `/appraisal/economicunits/${activeEconomicUnit}/` +
                    `${activeEconomicUnitYear}/${activeEconomicUnitVersion}/` +
                    `${activeEconomicUnitRollCorr}/${link}`
                  }
                  title={title}
                />
              ))
            )}
          </SidebarItem>
        )}
      </Sidebar>
      <ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper menu={buildMenuLink()}>
            <Header
              title={parseHeaderTitle(location.pathname, propertyMenuList.data)}
              toggleSidebar={toggleSidebar}
            >
              {property?.data?.pID &&
              recalculate?.get('state') === fetchingState.FETCHING ? (
                <div className="recalculating">Recalculating</div>
              ) : Object.keys(recalculationJob).length > 0 &&
                recalculationJob.recalcStatus !== 'Finished' &&
                !recalculationJob.error ? (
                <div style={{ width: 256 }} className="recalculating">
                  {'Recalculation Property & Smart Group…'}
                </div>
              ) : null}
              {printCard?.toJS()?.data && (
                <div style={{ width: 156 }} className="recalculating">{`${
                  printCard.toJS().data
                }…`}</div>
              )}
              <HeaderBackgroundImage />
              <div
                style={{
                  height: '100%',
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: usingHistoryDb
                    ? '#6c5939'
                    : 'rgba(255, 255, 255, 0.5)',
                  margin: 'auto',
                  marginTop: '8px',
                  marginRight: '5px',
                  zIndex: 1,
                  fontFamily: usingHistoryDb ? 'Times New Roman' : 'Arial',
                }}
              >
                {currentAppraisalYear}
              </div>
              {getAppraisalRoute(location.pathname) && !isReadOnly && (
                <HeaderItem
                  icon={plusIcon}
                  link={getAppraisalRoute(location.pathname).create}
                  title="Create"
                />
              )}
              {Object.keys(recalculationJob).length > 0 &&
                recalculationJob.recalcStatus !== 'Finished' &&
                !recalculationJob.error && (
                  <HeaderItem icon={abacusIconAnim} title="calc- ulating" />
                )}
              {activeProperty &&
                property &&
                property.data &&
                property.data.pID &&
                propertyDetails.data &&
                !!propertyDetails.data.isEditable &&
                !isReadOnly &&
                recalculate &&
                recalculate.get('state') !== fetchingState.FETCHING &&
                Object.keys(recalculationJob).length === 0 && (
                  <HeaderItem
                    icon={abacusIcon}
                    title="Re- calculate"
                    onClick={recalculatePropertyHandler}
                  />
                )}
              {activeEconomicUnit &&
                recalculate &&
                recalculate.get('state') !== fetchingState.FETCHING &&
                Object.keys(recalculationJob).length === 0 && (
                  <HeaderItem
                    icon={abacusIcon}
                    title="Re- calculate"
                    onClick={recalculateEconomicUnitHandler}
                  />
                )}
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              {canViewSubject('AppraisalSmartList') && (
                <HeaderItem
                  icon={versionIcon}
                  title="Smart List"
                  onClick={() => history.push('/appraisal/smart-lists')}
                />
              )}
              <HeaderItem icon={appsIcon} title="Apps" />
              <HeaderZendeskWidget />
            </Header>
            {isRecalculating === fetchingState.FETCHING && (
              <div
                style={{
                  position: 'absolute',
                  top: '63px',
                  zIndex: 12,
                  width: '100%',
                }}
              >
                <LinearProgress />
              </div>
            )}
          </HeaderWrapper>

          <div>{children}</div>
          <OwnerLookupModal />
          <OwnerCreateModal />
          <PropertyLookupModal />
          <DeedLookupModal />
          <ApplicationLookupModal />
        </SidebarPageWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

PageWrapper.propTypes = {
  activeAgent: PropTypes.string,
  activeOwner: PropTypes.string,
  activeProperty: PropTypes.string,
  activePropertyYear: PropTypes.string,
  activePropertyVersion: PropTypes.any,
  activePropertyRollCorr: PropTypes.any,
  activeSmartGroup: PropTypes.string,
  activeBuilderPlan: PropTypes.string,
  activeEconomicUnit: PropTypes.string,
  activeEconomicUnitYear: PropTypes.any,
  activeEconomicUnitVersion: PropTypes.any,
  activeEconomicUnitRollCorr: PropTypes.any,
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  currentAppraisalYear: PropTypes.string,
  history: PropTypes.object.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  persistPreference: PropTypes.func.isRequired,
  printCard: PropTypes.object,
  property: PropTypes.object,
  propertyMenuList: PropTypes.object,
  recalculate: ImmutablePropTypes.map,
  recalculationJob: PropTypes.object.isRequired,
  recalculateEconomicUnit: PropTypes.func.isRequired,
  recalculateProperty: PropTypes.func.isRequired,
  resetRecalculationJob: PropTypes.func.isRequired,
  checkRecalcProperty: PropTypes.func.isRequired,
  fetchPropertyErrors: PropTypes.func.isRequired,
  fetchRecalculatedValues: PropTypes.func.isRequired,
  fetchEconUnitRecalcValues: PropTypes.func.isRequired,
  selectedYear: PropTypes.string,
  setActiveTab: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  selectPropertyRecord: PropTypes.func.isRequired,
  selectPropertyRecordYear: PropTypes.func.isRequired,
  userRights: ImmutablePropTypes.list,
  fetchRecalculationJob: PropTypes.func.isRequired,
  fetchMostCurrentProperty: PropTypes.func.isRequired,
  quickPropertyLookup: PropTypes.func.isRequired,
  propertyDetails: PropTypes.object,
  userStatus: PropTypes.object,
  userData: PropTypes.object,
  selectedPersonalDetailID: PropTypes.any,
  fetchBppSubDetails: PropTypes.func.isRequired,
  isRecalculating: PropTypes.any,
  setViewOnlyMode: PropTypes.func.isRequired,
  recalculateTrigger: PropTypes.any,
};

const Wrapper = styled.div`
  .third-gen-item {
    a {
      padding-left: 50px;
    }
  }
`;

const LabelWrapper = styled.div`
  > div > span {
    font-size: 1.2em;
  }
`;

const mapStateToProps = (state) => ({
  activeProperty: selectedPropertyRecordSelector(state),
  activePropertyYear: selectedPropertyRecordYearSelector(state),
  activePropertyVersion: selectedPropertyPVersionSelector(state),
  activePropertyRollCorr: selectedPropertyPRollCorrSelector(state),
  activeAgent: selectedAgentRecordSelector(state),
  activeOwner: selectedOwnerRecordSelector(state),
  activeSmartGroup: selectedSmartGroupSelector(state),
  activeBuilderPlan: selectedBuilderPlanSelector(state),
  activeEconomicUnit: selectedEconomicUnitSelector(state),
  activeEconomicUnitYear: selectedEconomicUnitYearSelector(state),
  activeEconomicUnitVersion: selectedEconomicUnitVersionSelector(state),
  activeEconomicUnitRollCorr: selectedEconomicUnitRollCorrSelector(state),
  currentAppraisalYear: currentAppraisalYearSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  printCard: printCardSelector(state),
  property:
    propertyHeaderSelector(state) && propertyHeaderSelector(state).toJS(),
  propertyMenuList:
    propertyMenulistSelector(state) && propertyMenulistSelector(state).toJS(),
  propertyDetails: propertySelector(state) && propertySelector(state).toJS(),
  userStatus: userStatusSelector(state),
  userData: userDataSelector(state),
  recalculate: propertyRecalculateSelector(state),
  recalculationJob: getRecalculationJob(state),
  selectedYear: selectedYearSelector(state),
  userRights: userRightsSelector(state),
  selectedPersonalDetailID: selectedPersonalDetailIDSelector(state),
  isRecalculating: isRecalculatingSelector(state),
  recalculateTrigger: recalculateTriggerSelector(state),
});

const mapDispatchToProps = {
  fetchCurrentAppraisalYear: globalAppraisalActions.fetchCurrentAppraisalYear,
  fetchRecalculationJob: propertyActions.fetchRecalculationJob,
  recalculateEconomicUnit: economicUnitActions.recalculateEconomicUnit,
  recalculateProperty: propertyActions.recalculateProperty,
  resetRecalculationJob: propertyActions.resetRecalculationJob,
  setActiveProperty: globalAppraisalActions.selectPropertyRecord,
  selectYear: globalAppraisalActions.selectYear,
  toggleSidebar: globalActions.toggleSidebar,
  checkRecalcProperty: propertyActions.checkRecalcProperty,
  fetchPropertyErrors: propertyActions.fetchPropertyErrors,
  fetchRecalculatedValues: propertyActions.fetchRecalculatedValues,
  fetchEconUnitRecalcValues: economicUnitActions.fetchRecalculatedValues,
  fetchMostCurrentProperty: propertyActions.fetchMostCurrentProperty,
  quickPropertyLookup: propertyActions.quickPropertyLookup,
  persistPreference: globalAppraisalActions.persistPreference,
  selectPropertyRecord: globalAppraisalActions.selectPropertyRecord,
  selectPropertyRecordYear: globalAppraisalActions.selectPropertyRecordYear,
  fetchBppSubDetails: propertyActions.fetchBppSubDetails,
  setViewOnlyMode: globalAppraisalActions.setViewOnlyMode,
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab:
      (_, { setActiveProperty }) =>
      () => {
        setActiveProperty('');
        return { activeTab: 0 };
      },
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchCurrentAppraisalYear();
    },
    componentDidUpdate(prevProps) {
      if (
        (this.props.activeProperty !== prevProps.activeProperty &&
          this.props.activeProperty) ||
        (this.props.activeOwner !== prevProps.activeOwner &&
          this.props.activeOwner) ||
        (this.props.activeAgent !== prevProps.activeAgent &&
          this.props.activeAgent) ||
        (this.props.activeSmartGroup !== prevProps.activeSmartGroup &&
          this.props.activeSmartGroup) ||
        (this.props.activeBuilderPlan !== prevProps.activeBuilderPlan &&
          this.props.activeBuilderPlan) ||
        (this.props.activeEconomicUnit !== prevProps.activeEconomicUnit &&
          this.props.activeEconomicUnit)
      ) {
        this.props.setActiveTab(null, 1);
      }
    },
  }),
);

export default enhance(PageWrapper);
